import { useEffect, useState } from "react";
import api from "../services/api";

// ── Config ────────────────────────────────────────────────────────────────────

const QUALITY_CONFIG = {
  green:  { label: "High",    bar: "bg-green-500",  badge: "bg-green-100 text-green-700",  dot: "bg-green-500",  pct: 100 },
  yellow: { label: "Medium",  bar: "bg-yellow-400", badge: "bg-yellow-100 text-yellow-700",dot: "bg-yellow-400", pct: 55  },
  red:    { label: "Low",     bar: "bg-red-500",    badge: "bg-red-100 text-red-600",      dot: "bg-red-500",    pct: 20  },
  gray:   { label: "Unknown", bar: "bg-gray-300",   badge: "bg-gray-100 text-gray-500",    dot: "bg-gray-300",   pct: 0   },
};

// All milestone tiers shown as pill buttons
const TIER_STEPS = [
  { key: "TIER_1K",        label: "1K",        sub: "1,000/day"   },
  { key: "TIER_2K",        label: "2K",        sub: "2,000/day"   },
  { key: "TIER_10K",       label: "10K",       sub: "10,000/day"  },
  { key: "TIER_100K",      label: "100K",      sub: "100,000/day" },
  { key: "TIER_UNLIMITED", label: "Unlimited", sub: "No limit"    },
];

// ── Component ─────────────────────────────────────────────────────────────────

const WhatsAppHealth = () => {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    api.get("/business-profile/phone-health")
      .then((r) => setData(r.data.data))
      .catch((err) => setError(err?.response?.data?.error || "Failed to load"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 animate-pulse">
        <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
        <div className="h-3 bg-gray-100 rounded w-1/2" />
      </div>
    );
  }

  if (error || !data) {
    return null; // silently hide if Meta credentials aren't set up
  }

  const quality     = QUALITY_CONFIG[data.qualityInfo?.color] || QUALITY_CONFIG.gray;
  const currentTier = data.messagingTier;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 sm:p-5">
      {/* Header row */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {/* WhatsApp icon */}
          <div className="w-7 h-7 rounded-lg bg-[#25d366]/10 flex items-center justify-center flex-shrink-0">
            <svg className="w-4 h-4 text-[#25d366]" fill="currentColor" viewBox="0 0 24 24">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
          </div>
          <p className="text-sm font-semibold text-gray-800">WhatsApp Account Health</p>
        </div>
        {data.displayPhone && (
          <span className="text-xs text-gray-400 font-mono hidden sm:block">{data.displayPhone}</span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/* ── Quality Rating ── */}
        <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide">Message Quality</p>
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${quality.badge}`}>
              {quality.label}
            </span>
          </div>
          {/* Progress bar */}
          <div className="bg-gray-200 rounded-full h-2 overflow-hidden mb-1.5">
            <div
              className={`h-full rounded-full transition-all duration-700 ${quality.bar}`}
              style={{ width: `${quality.pct}%` }}
            />
          </div>
          <p className="text-[11px] text-gray-400">
            Based on customer feedback and block rates
          </p>
        </div>

        {/* ── Messaging Tier ── */}
        <div className="bg-gray-50 rounded-xl p-3.5 border border-gray-100">
          <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-2">Daily Limit</p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {TIER_STEPS.map((step) => {
              const isCurrent = step.key === currentTier;
              return (
                <span
                  key={step.key}
                  title={step.sub}
                  className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${
                    isCurrent
                      ? "bg-blue-600 text-white border-blue-600"
                      : "bg-white text-gray-400 border-gray-200"
                  }`}
                >
                  {step.label}
                </span>
              );
            })}
          </div>
          <p className="text-[11px] text-gray-400">
            Number of new customer conversations you can start in 24 hours
          </p>
        </div>

      </div>

      {/* Footer note */}
      {data.verifiedName && (
        <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-gray-50">
          <div className="w-3.5 h-3.5 rounded-full bg-[#25d366] flex items-center justify-center flex-shrink-0">
            <svg className="w-2 h-2 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-xs text-gray-500">
            Verified as <span className="font-medium text-gray-700">{data.verifiedName}</span>
          </p>
        </div>
      )}
    </div>
  );
};

export default WhatsAppHealth;
